/**
 * Custom templates — user-defined starting-point configs for new models.
 *
 * A template snapshots the reusable config bits of a hydraulic model
 * (fluid zones, pipe specifications, design constraints, calculation
 * settings) so an engineer can save a common workflow like
 * "Glycol 30% chilled water with Sch 10S CS" once and apply it to
 * future projects in one click.
 *
 * Intentionally excluded from the snapshot:
 *   - vertices and edges (a template is a starting point, not a model)
 *   - document_header / authorship (rebuilt per new model)
 *   - application_type (used for built-in presets only)
 *   - calculation_result and any solver state
 *
 * Tier 1 persists these to localStorage per browser. Tier 2 moves the
 * same shape behind a tenant-scoped API. The types in this file are
 * shared between tiers so the upgrade path doesn't require type
 * migration.
 */

import { z } from 'zod';
import { FluidZoneSchema } from '../hydraulic/document/fluid-zone';
import { PipeSpecificationSchema } from '../hydraulic/document/pipe-specification';
import { CalculationSettingsSchema } from '../hydraulic/document/calculation-settings';
import { DesignConstraintsSchema } from '../hydraulic/document/design-constraints';

/**
 * Schema version of the underlying HydraulicSystem. Bumped whenever
 * fluid_zones / pipe_specifications / design_constraints / calculation_settings
 * shapes change in a way that would invalidate previously-saved templates.
 *
 * The UI uses this to grey out templates whose schema version doesn't
 * match the currently-running system — the user is asked to recreate
 * them rather than risk silent data loss from an auto-migration.
 */
export const CURRENT_TEMPLATE_SCHEMA_VERSION = 2 as const;

/**
 * Payload version — the shape of THIS file's template wrapper, not the
 * underlying HydraulicSystem. Bump when adding/removing top-level fields
 * from TemplatePayload. Allows future migrations without losing data.
 */
export const CURRENT_TEMPLATE_PAYLOAD_VERSION = 1 as const;

/** The config slice captured by a custom template. */
export const TemplatePayloadSchema = z.object({
  /** Wrapper version — not the HydraulicSystem schema version. */
  version: z.literal(CURRENT_TEMPLATE_PAYLOAD_VERSION),
  /** HydraulicSystem schema version this template was saved against. */
  schema_version: z.literal(CURRENT_TEMPLATE_SCHEMA_VERSION),
  /**
   * At least one fluid zone — a model with zero fluid zones is
   * semantically invalid (edges would have nothing to reference).
   */
  fluid_zones: z.array(FluidZoneSchema).min(1),
  /** At least one pipe specification for the same reason. */
  pipe_specifications: z.array(PipeSpecificationSchema).min(1),
  /** Optional — not every template needs design constraints. */
  design_constraints: DesignConstraintsSchema.optional(),
  /** Required — every model needs calculation settings to solve. */
  calculation_settings: CalculationSettingsSchema,
});

export type TemplatePayload = z.infer<typeof TemplatePayloadSchema>;

/** Full template record — what's stored and what flows through the API. */
export const CustomTemplateSchema = z.object({
  /** UUIDv4. Generated client-side in Tier 1, server-side in Tier 2. */
  id: z.string().uuid(),
  /** Display name — trimmed, non-empty, unique within a user/tenant. */
  name: z.string().min(1).max(80),
  /** Optional description — free-form. */
  description: z.string().max(280).optional(),
  /** ISO-8601 timestamp. */
  createdAt: z.string(),
  /**
   * UUID of the creating user. Undefined in Tier 1 (no user context in
   * localStorage); populated in Tier 2 where the backend sets it from
   * the authenticated session.
   */
  createdBy: z.string().optional(),
  /** The serialized config slice — see TemplatePayloadSchema. */
  payload: TemplatePayloadSchema,
});

export type CustomTemplate = z.infer<typeof CustomTemplateSchema>;

/**
 * Input shape when creating a new template. Fields the system fills in
 * (id, createdAt, createdBy) are absent; the payload version is
 * hard-coded by the snapshot helper.
 */
export type CustomTemplateDraft = Omit<CustomTemplate, 'id' | 'createdAt' | 'createdBy'>;
